import React, { useState, useRef, useEffect } from 'react';
import { useAdmin } from '../context/AdminContext';
import { Edit2, X, Check, Upload, RotateCcw, Type, Image as ImageIcon, Link as LinkIcon } from 'lucide-react';
import { API_URL } from '../config';



// ==================== Edit Popup Component ====================
const EditPopup = ({ item, onSave, onClose, onReset, uploadImage }) => {
  const [value, setValue] = useState(item?.value || {});
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);
  const popupRef = useRef(null);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleSave = async () => {
    setSaving(true);
    await onSave(item.id, value);
    setSaving(false);
    onClose();
  };

  const handleReset = async () => {
    if (window.confirm('Сбросить к значению по умолчанию?')) {
      await onReset(item.id);
      onClose();
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => setPreviewUrl(e.target.result);
    reader.readAsDataURL(file);
    
    setSaving(true);
    const url = await uploadImage(file);
    if (url) {
      setValue({ ...value, url });
    }
    setSaving(false);
  };

  const renderFields = () => {
    if (item.type === 'image') {
      return (
        <div className="space-y-3">
          {/* Preview */}
          {(previewUrl || value?.url) && (
            <div className="bg-gray-100 rounded-lg p-2 flex justify-center">
              <img 
                src={previewUrl || value.url} 
                alt="Preview" 
                className="max-h-40 object-contain rounded"
              />
            </div>
          )}
          
          {/* Upload button */}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg border-2 border-dashed border-purple-200 transition-colors"
          >
            {saving ? (
              <div className="animate-spin w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full" />
            ) : (
              <>
                <Upload className="w-5 h-5" />
                Загрузить с компьютера
              </>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden"
          />
          
          {/* URL input */}
          <div className="flex gap-2">
            <input
              type="url"
              value={value?.url || ''}
              onChange={(e) => {
                setValue({ ...value, url: e.target.value });
                setPreviewUrl(e.target.value);
              }}
              placeholder="Или вставьте URL изображения"
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-purple-500"
            />
          </div>
        </div>
      );
    }

    if (item.type === 'link') {
      return (
        <div className="space-y-2">
          <label className="text-sm text-gray-600">URL ссылки</label>
          <input
            type="url"
            value={value?.url || value || ''}
            onChange={(e) => setValue({ url: e.target.value })}
            placeholder="https://..."
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green-500"
          />
        </div>
      );
    }

    // Text type - show multilingual fields
    return (
      <div className="space-y-3">
        {['ru', 'kz', 'en'].map(lang => (
          <div key={lang} className="flex items-start gap-2">
            <span className="w-10 py-2 text-xs font-bold text-gray-500 uppercase bg-gray-100 rounded text-center">
              {lang}
            </span>
            {(value?.[lang]?.length > 50 || item.label?.includes('Описание')) ? (
              <textarea
                value={value?.[lang] || ''}
                onChange={(e) => setValue({ ...value, [lang]: e.target.value })}
                placeholder={`Текст на ${lang === 'ru' ? 'русском' : lang === 'kz' ? 'казахском' : 'английском'}`}
                rows={3}
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500 resize-y"
              />
            ) : (
              <input
                type="text"
                value={value?.[lang] || value?.text || ''}
                onChange={(e) => setValue({ ...value, [lang]: e.target.value })}
                placeholder={`Текст на ${lang === 'ru' ? 'русском' : lang === 'kz' ? 'казахском' : 'английском'}`}
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
              />
            )}
          </div>
        ))}
        {/* Single value field for non-multilingual text */}
        {value?.text !== undefined && (
          <div className="flex items-center gap-2">
            <span className="w-10 py-2 text-xs font-bold text-gray-500 uppercase bg-gray-100 rounded text-center">
              Все
            </span>
            <input
              type="text"
              value={value?.text || ''}
              onChange={(e) => setValue({ ...value, text: e.target.value })}
              placeholder="Значение"
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
            />
          </div>
        )}
      </div>
    );
  };

  const TypeIcon = item.type === 'image' ? ImageIcon : item.type === 'link' ? LinkIcon : Type;
  const typeColor = item.type === 'image' ? 'purple' : item.type === 'link' ? 'green' : 'blue';

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div 
        ref={popupRef}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`px-5 py-4 bg-gradient-to-r from-${typeColor}-500 to-${typeColor}-600 text-white`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <TypeIcon className="w-5 h-5" />
              <div>
                <h3 className="font-semibold">{item.label}</h3>
                <p className="text-xs opacity-80">{item.section} / {item.key}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-lg transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-5">
          {renderFields()}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 bg-gray-50 border-t flex justify-between">
          <button
            onClick={handleReset}
            className="flex items-center gap-1 px-3 py-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors text-sm"
          >
            <RotateCcw className="w-4 h-4" />
            Сбросить
          </button>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors text-sm"
            >
              Отмена
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className={`flex items-center gap-1 px-4 py-2 bg-${typeColor}-500 hover:bg-${typeColor}-600 text-white rounded-lg transition-colors text-sm font-medium`}
            >
              {saving ? (
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              Сохранить
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== Editable Wrapper Component ====================
export const Editable = ({ 
  contentId,
  section,
  contentKey,
  children,
  className = ''
}) => {
  const { isEditMode, siteContent, saveContent, resetToDefault, uploadImage } = useAdmin();
  const [showPopup, setShowPopup] = useState(false);

  // Get content item from CMS
  const item = siteContent[section]?.[contentKey];

  if (!isEditMode) {
    return children;
  }

  // If no CMS item found, just show children without edit capability
  if (!item) {
    return (
      <div className={`relative group ${className}`}>
        {children}
        <div className="absolute -top-1 -right-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
          <span className="text-xs bg-gray-400 text-white px-1 py-0.5 rounded">
            Не в CMS
          </span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div 
        className={`relative cursor-pointer group ${className}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setShowPopup(true);
        }}
      >
        {/* Content */}
        {children}
        
        {/* Hover overlay */}
        <div className="absolute inset-0 border-2 border-blue-500 border-dashed rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
        
        {/* Edit badge */}
        <div className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
          <div className="bg-blue-500 text-white p-1.5 rounded-full shadow-lg">
            <Edit2 className="w-3 h-3" />
          </div>
        </div>
      </div>

      {/* Edit popup */}
      {showPopup && (
        <EditPopup
          item={{ ...item, id: item.id || contentId }}
          onSave={saveContent}
          onClose={() => setShowPopup(false)}
          onReset={resetToDefault}
          uploadImage={uploadImage}
        />
      )}
    </>
  );
};

// ==================== Simple Editable Text ====================
export const EditableText = ({ 
  contentId,
  section,
  contentKey,
  defaultValue, 
  language = 'ru',
  className = '',
  as: Component = 'span',
  multiline = false,
  placeholder = 'Введите текст...'
}) => {
  const { isEditMode, siteContent, saveContent, resetToDefault, uploadImage } = useAdmin();
  const [showPopup, setShowPopup] = useState(false);
  
  const getStringValue = (val) => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'object') {
      if (val[language]) return val[language];
      if (val.text) return val.text;
      const values = Object.values(val);
      return values.length > 0 ? String(values[0]) : '';
    }
    return String(val);
  };
  
  // Get value from content system or use default
  const item = siteContent[section]?.[contentKey];
  const storedValue = item?.value?.[language] || item?.value?.text;
  const displayValue = storedValue || getStringValue(defaultValue);

  if (!isEditMode) {
    return <Component className={className}>{displayValue}</Component>;
  }

  return (
    <>
      <Component
        className={`${className} cursor-pointer relative group hover:outline hover:outline-2 hover:outline-blue-500 hover:outline-offset-2 rounded transition-all`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (item) setShowPopup(true);
        }}
      >
        {displayValue || placeholder}
        {item && (
          <span className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
            <Edit2 className="w-5 h-5 text-blue-500 bg-white rounded-full p-1 shadow-lg border border-blue-200" />
          </span>
        )}
        {!item && (
          <span className="absolute -top-1 -right-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
            <span className="text-xs bg-gray-400 text-white px-1 py-0.5 rounded">?</span>
          </span>
        )}
      </Component>

      {showPopup && item && (
        <EditPopup
          item={item}
          onSave={saveContent}
          onClose={() => setShowPopup(false)}
          onReset={resetToDefault}
          uploadImage={uploadImage}
        />
      )}
    </>
  );
};

// ==================== Editable Image ====================
export const EditableImage = ({
  contentId,
  section,
  contentKey,
  src,
  alt,
  className = ''
}) => {
  const { isEditMode, siteContent, saveContent, resetToDefault, uploadImage } = useAdmin();
  const [showPopup, setShowPopup] = useState(false);

  const item = siteContent[section]?.[contentKey];
  const imageSrc = item?.value?.url || src;

  if (!isEditMode) {
    return <img src={imageSrc} alt={alt} className={className} />;
  }

  return (
    <>
      <div 
        className="relative group cursor-pointer"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (item) setShowPopup(true);
        }}
      >
        <img src={imageSrc} alt={alt} className={`${className} group-hover:opacity-80 transition-opacity`} />
        
        {/* Overlay */}
        <div className="absolute inset-0 border-2 border-purple-500 border-dashed rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
        
        {/* Edit badge */}
        {item && (
          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="bg-purple-500 text-white px-2 py-1 rounded-lg shadow-lg flex items-center gap-1 text-sm">
              <Edit2 className="w-3 h-3" />
              Изменить
            </div>
          </div>
        )}
      </div>

      {showPopup && item && (
        <EditPopup
          item={item}
          onSave={saveContent}
          onClose={() => setShowPopup(false)}
          onReset={resetToDefault}
          uploadImage={uploadImage}
        />
      )}
    </>
  );
};

// ==================== Editable Link ====================
export const EditableLink = ({
  contentId,
  section,
  contentKey,
  href,
  children,
  className = '',
  target = '_self'
}) => {
  const { isEditMode, siteContent, saveContent, resetToDefault, uploadImage } = useAdmin();
  const [showPopup, setShowPopup] = useState(false);

  const item = siteContent[section]?.[contentKey];
  const linkHref = item?.value?.url || href;

  if (!isEditMode) {
    return (
      <a href={linkHref} target={target} className={className}>
        {children}
      </a>
    );
  }

  return (
    <>
      <span
        className={`${className} cursor-pointer relative group hover:outline hover:outline-2 hover:outline-green-500 hover:outline-offset-2 rounded transition-all`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (item) setShowPopup(true);
        }}
      >
        {children}
        {item && (
          <span className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
            <LinkIcon className="w-5 h-5 text-green-500 bg-white rounded-full p-1 shadow-lg border border-green-200" />
          </span>
        )}
      </span>

      {showPopup && item && (
        <EditPopup
          item={item}
          onSave={saveContent}
          onClose={() => setShowPopup(false)}
          onReset={resetToDefault}
          uploadImage={uploadImage}
        />
      )}
    </>
  );
};

export default EditableText;
